import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHead, Panel, SButton, Empty, sInput, softDelete } from "@/components/staff/Bits";

const ZONES = ["Zone 1", "Zone 2", "Zone 3", "Zone 4", "Zone 5", "Zone 6"];

function GkkRow({ g, upd, reload }) {
  const rename = () => { const n = window.prompt("Correct GKK name (from the verified parish directory):", g.name); if (n && n.trim()) upd(g, { name: n }); };
  const readdress = () => { const a = window.prompt("Correct address:", g.address || ""); if (a !== null) upd(g, { address: a }); };
  return (
    <li data-testid={`gkk-row-${g.code || g.name}`} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
      <div className="min-w-0">
        <p className={g.active ? "font-semibold" : "text-[#605349] line-through"}>{g.code && <span className="font-mono text-xs text-[#605349] mr-2">{g.code}</span>}{g.name}</p>
        {g.address && <p className="text-sm text-[#605349]">{g.address}</p>}
      </div>
      <span className="flex flex-wrap gap-2 shrink-0">
        <SButton variant="ghost" data-testid={`gkk-rename-${g.code || g.name}`} onClick={rename}>Rename</SButton>
        <SButton variant="ghost" data-testid={`gkk-address-${g.code || g.name}`} onClick={readdress}>Address</SButton>
        <SButton variant="ghost" data-testid={`gkk-toggle-${g.code || g.name}`} onClick={() => upd(g, { active: !g.active })}>{g.active ? "Deactivate" : "Activate"}</SButton>
        <SButton variant="danger" data-testid={`gkk-delete-${g.code || g.name}`} onClick={async () => { if (await softDelete("gkk", g.id, `GKK ${g.name}`)) reload(); }}>Delete</SButton>
      </span>
    </li>
  );
}

export default function Gkks() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", zone: "", code: "", address: "" });
  const load = useCallback(() => { api.get("/gkks").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/gkks", { name: f.name, active: true, zone: f.zone || null, code: f.code || null, address: f.address || null }); setF({ name: "", zone: "", code: "", address: "" }); toast.success("GKK added"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upd = async (g, body) => { try { await api.patch(`/gkks/${g.id}`, { name: g.name, active: g.active, ...body }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const zones = [...new Set(list.map((g) => g.zone || "Other"))];
  return (
    <div className="max-w-4xl">
      <PageHead title="GKK directory" sub="Official parish GKK roster (imported from 'Updated and Corrected List of GKKs'). Patron names repeat, so each GKK is identified by its ID and local area. GKK membership never grants access to case records." />
      <Panel testid="gkk-add-panel" title="Add a GKK">
        <div className="grid sm:grid-cols-4 gap-2">
          <input data-testid="gkk-code-input" className={sInput} placeholder="GKK ID (e.g. GKK-057)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
          <select data-testid="gkk-zone-select" className={sInput} value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })}><option value="">Zone…</option>{ZONES.map((z) => <option key={z}>{z}</option>)}</select>
          <input data-testid="gkk-name-input" className={`${sInput} sm:col-span-2`} placeholder="Name – Local area (Zone)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="gkk-address-input" className={`${sInput} sm:col-span-3`} placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          <SButton data-testid="gkk-add-button" disabled={!f.name.trim()} onClick={add}>Add</SButton>
        </div>
      </Panel>
      {list.length === 0 ? <Panel className="mt-5"><Empty testid="gkk-empty">No GKKs yet.</Empty></Panel> : zones.map((z) => (
        <Panel key={z} className="mt-5" title={`${z} (${list.filter((g) => (g.zone || "Other") === z).length})`} testid={`gkk-zone-panel-${z}`}>
          <ul className="divide-y divide-[#F1EBE3]">{list.filter((g) => (g.zone || "Other") === z).map((g) => <GkkRow key={g.id} g={g} upd={upd} reload={load} />)}</ul>
        </Panel>))}
    </div>
  );
}
